import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useBoards } from '@/hooks/useBoards.js';
import { useProjects } from '@/hooks/useProjects.js';
import { PageHeader } from '@/components/PageHeader.js';
import { LoadingState, ErrorState, EmptyState } from '@/components/PageState.js';

export function BoardList() {
  const { data: boards, isLoading, error } = useBoards();
  const { data: projects } = useProjects();

  const projectByKey = useMemo(
    () => new Map((projects ?? []).map((p) => [p.key, p])),
    [projects],
  );

  if (isLoading) return <LoadingState label="Loading boards..." />;
  if (error) return <ErrorState message="Failed to load boards" />;

  return (
    <div>
      <PageHeader title="Boards" meta={boards ? `${boards.length}` : undefined} />
      {!boards || boards.length === 0 ? (
        <EmptyState title="No boards found" />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
          {boards.map((board) => {
            const project = board.projectKey ? projectByKey.get(board.projectKey) : undefined;
            return (
              <Link
                key={board.id}
                to={`/boards/${board.id}`}
                className="group flex items-center gap-3 rounded-lg border bg-white p-4 transition-all hover:border-blue-300 hover:shadow-md"
              >
                {project?.avatarUrl ? (
                  <img src={project.avatarUrl} alt="" className="h-10 w-10 shrink-0 rounded-md" />
                ) : (
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-gray-100 text-gray-500" aria-hidden="true">
                    <svg viewBox="0 0 16 16" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={1.5}>
                      <rect x="2" y="2.5" width="3.5" height="11" rx="1" />
                      <rect x="6.25" y="2.5" width="3.5" height="7" rx="1" />
                      <rect x="10.5" y="2.5" width="3.5" height="9" rx="1" />
                    </svg>
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <h2 className="truncate font-semibold text-gray-900 group-hover:text-blue-700">{board.name}</h2>
                  <div className="mt-0.5 flex items-center gap-2 text-xs text-gray-500">
                    <span className="rounded-[3px] bg-gray-100 px-1.5 py-px font-semibold uppercase tracking-wide text-gray-600">{board.type}</span>
                    {project ? <span className="truncate">{project.name}</span> : board.projectKey && <span>{board.projectKey}</span>}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
